/**
 * Deterministic error-code generation.
 *
 * Each error surfaces a copyable code (rule 5: "Support never has to ask 'what
 * time roughly?'"). The code is derived from the error message + the most
 * useful frame of the component stack, so the SAME bug always produces the
 * SAME code — easy to search in logs and in Sentry.
 *
 * Format: `err_<context>_<4-hex>`. The hex suffix is a short hash, not a
 * cryptographic identifier — just enough to disambiguate similar errors.
 */

/**
 * Stable 32-bit FNV-1a hash. Tiny + deterministic + zero-dep. Good enough
 * to give two distinct errors distinct 4-hex suffixes.
 */
function fnv1a(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    // 32-bit FNV prime multiplication via shift-add.
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
  }
  return hash >>> 0;
}

function firstUsefulStackFrame(stack: string | undefined): string {
  if (!stack) return '';
  // Skip the first line (it's the message itself) and pick the first frame
  // that points at our code, not into react-dom / next internals.
  const lines = stack.split('\n').slice(1);
  for (const line of lines) {
    if (!line.includes('node_modules') && !line.includes('react-dom') && line.trim()) {
      return line.trim();
    }
  }
  return lines[0]?.trim() ?? '';
}

/**
 * Generate an error code for a thrown error.
 *
 * `context` is a short slug describing where the boundary lives —
 * `pm_render` for the root, `dash_load` for the dashboard route,
 * `widget_<name>` for a card, etc. Same context + same error message
 * always produces the same code.
 */
export function errorCode(context: string, error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  const frame = error instanceof Error ? firstUsefulStackFrame(error.stack) : '';
  const hash = fnv1a(`${message}|${frame}`).toString(16).padStart(8, '0').slice(0, 4);
  const slug = context.replace(/[^a-z0-9_]/gi, '_').toLowerCase().slice(0, 20);
  return `err_${slug}_${hash}`;
}
